import React, { useState, useEffect, useRef } from 'react';
import {
  MONTH_LIST,
  DAYS_LIST_GREGORIAN,
  DAYS_LIST_JALALI,
  DAYS_LIST_GREGORIAN_LEAP_YEAR,
  DAYS_LIST_JALALI_LEAP_YEAR,
  month_30days,
  isLeapYear_Jalali,
  isLeapYear_Gregorian,
} from './select-date-dropdown.constants';
import './select-date-dropdown.scss';
import { Select } from '../../../component';
import { colors } from '../../../utils/consts/color-variable';
import { Conditional } from '../../../utils/consts';
import { CalendarIcon } from '../../../content/svg-component/calendar/calendarIcon';
import momentjs from 'moment-jalaali';

const DEFAULT_CALENDAR_TYPE = 'jalali';
export const SelectDateDropdown = ({
  label,
  labelClassName = '',
  containerClassName,
  dateDirection,
  yearsToCalc,
  value,
  onChange,
  language,
  formatMessage,
}) => {
  //#region states
  const [isLeapYear, setIsLeapYear] = useState(false);
  const [yearsList, setYearsList] = useState([
    { label: new Date().getFullYear(), value: new Date().getFullYear() },
  ]);
  const [monthsList, setMonthsList] = useState(
    MONTH_LIST[DEFAULT_CALENDAR_TYPE],
  );
  const [daysList, setDaysList] = useState(month_30days);
  const [selectedYear, setSelectedYear] = useState('');
  const [selectedMonth, setSelectedMonth] = useState('');
  const [selectedDay, setSelectedDay] = useState('');
  const [calendarType, setCalendarType] = useState(
    language.name === 'fa' ? 'jalali' : 'gregorian',
  );
  const [isDateComplete, setIsDateComplete] = useState(false);

  // const prevValue = useRef(
  //   value
  //     ? typeof value === 'string'
  //       ? new Date(value)?.toLocaleString().split(',')[0]
  //       : value
  //     : '',
  // );
  const prevValue = useRef(value ? value : '');
  //#endregion states

  //#region lifecycle
  useEffect(() => {
    setCalendarType(language.name === 'fa' ? 'jalali' : 'gregorian');
  }, [language.name]);
  useEffect(() => {
    generateYearsList(null);
  }, [yearsToCalc, dateDirection]);
  useEffect(() => {
    if (!!value) {
      const daysLst = generateDaysList(value.split('-')[1]);
      setDaysList(daysLst);
      switch (calendarType) {
        case 'gregorian':
          setSelectedYear(value.split('-')[0]);
          setSelectedMonth(value.split('-')[1]);
          setSelectedDay(value.split('-')[2]);
          break;

        default:
          const jDate = momentjs(value, 'YYYY-MM-DD')
            .format('jYYYY/jMM/jDD')
            .split('/');
          setSelectedYear(jDate[0]);
          setSelectedMonth(jDate[1]);
          setSelectedDay(jDate[2]);
          break;
      }
      setIsDateComplete(true);
      if (prevValue.current !== value) {
        onChange(value);
        prevValue.current = value;
      }
    } else {
      setSelectedYear('');
      setSelectedMonth('');
      setSelectedDay('');
      generateYearsList(calendarType);
      setMonthsList(MONTH_LIST[calendarType]);
    }
  }, [value]);
  //#endregion lifecyle

  //#region calc functions

  const generateDaysList = (month) => {
    let properDaysList;
    if (isLeapYear) {
      properDaysList =
        calendarType === 'jalali'
          ? DAYS_LIST_JALALI_LEAP_YEAR
          : DAYS_LIST_GREGORIAN_LEAP_YEAR;
    } else {
      properDaysList =
        calendarType === 'jalali' ? DAYS_LIST_JALALI : DAYS_LIST_GREGORIAN;
    }
    return properDaysList[Number(month)];
  };

  const generateYearsList = (type) => {
    const currentCalendarType = !!type ? type : calendarType;
    const currentYear =
      currentCalendarType === 'gregorian'
        ? new Date().getFullYear()
        : +new Intl.DateTimeFormat('en-US-u-ca-persian', {
            year: 'numeric',
          })
            .format(new Date())
            .split(' ')[0];
    const limitingYear =
      dateDirection === 'past'
        ? currentYear - (yearsToCalc - 1)
        : currentYear + (yearsToCalc - 1);
    const list = returnYearsList(currentYear, limitingYear);
    let yearsList = list.map((year) => {
      return { value: year, label: year };
    });
    if (dateDirection === 'past') {
      yearsList = yearsList.reverse();
    }
    setYearsList(yearsList);
  };
  const returnYearsList = (currentYear, limitingYear) => {
    const start = Math.min(currentYear, limitingYear);
    const end = Math.max(currentYear, limitingYear);
    const list = Array.from(
      { length: end - start + 1 },
      (_, index) => start + index,
    );
    return list;
  };
  const convertJalaliToGregorian = () => {
    const date = `${selectedYear}/${selectedMonth}/${selectedDay}`;
    const gDate = momentjs(date, 'jYYYY/jMM/jDD')
      .format('YYYY/MM/DD')
      .split('/');
    setSelectedYear(gDate[0]);
    setSelectedMonth(gDate[1]);
    setSelectedDay(gDate[2]);
    setCalendarType('jalali');
  };
  const convertGregorianToJalali = () => {
    const date = `${selectedYear}/${selectedMonth}/${selectedDay}`;
    const jDate = momentjs(date, 'YYYY/MM/DD')
      .format('jYYYY/jMM/jDD')
      .split('/');
    setSelectedYear(jDate[0]);
    setSelectedMonth(jDate[1]);
    setSelectedDay(jDate[2]);
    setCalendarType('gregorian');
  };
  //#endregion calc functions

  //#region actions
  const handleYearChange = (event) => {
    const year = event?.target?.value;
    let properDaysList = daysList;
    const isLeapYear =
      calendarType === 'jalali'
        ? isLeapYear_Jalali(+year)
        : isLeapYear_Gregorian(+year);
    switch (true) {
      case calendarType === 'gregorian' && selectedMonth == '2':
        properDaysList = isLeapYear
          ? DAYS_LIST_GREGORIAN_LEAP_YEAR['2']
          : DAYS_LIST_GREGORIAN['2'];
        break;
      case calendarType === 'jalali' && selectedMonth == '12':
        properDaysList = isLeapYear
          ? DAYS_LIST_JALALI_LEAP_YEAR['12']
          : DAYS_LIST_JALALI['12'];
        break;
      default:
        break;
    }
    setDaysList(properDaysList);
    setSelectedDay(+selectedDay > properDaysList.length ? '' : selectedDay);
    setIsLeapYear(isLeapYear);
    setSelectedYear(year);
    if (!!selectedMonth && !!selectedDay) {
      setIsDateComplete(true);
      onChange(returnFullDate(year, 'year'));
    }
  };
  const handleMonthChange = (event) => {
    const month = event?.target?.value;
    const daysLst = generateDaysList(month);
    if (!!selectedDay && +selectedDay > daysLst.length) setSelectedDay('');
    setDaysList(daysLst);
    setSelectedMonth(month);

    if (!!selectedYear && !!selectedDay) {
      setIsDateComplete(true);
      onChange(returnFullDate(month, 'month'));
    }
  };
  const handleDayChange = (event) => {
    const day = event?.target?.value;
    setSelectedDay(day);
    if (!!selectedMonth && !!selectedYear) {
      setIsDateComplete(true);
      onChange(returnFullDate(day, 'day'));
    }
  };
  const handleCalendarTypeChange = (type) => {
    generateYearsList(type);
    setMonthsList(MONTH_LIST[type]);
    if (isDateComplete) {
      switch (type) {
        case 'gregorian':
          if (calendarType !== type) convertJalaliToGregorian();
          break;

        case 'jalali':
          if (calendarType !== type) convertGregorianToJalali();
          break;
        default:
          break;
      }
    } else {
      setSelectedYear('');
      setSelectedMonth('');
      setSelectedDay('');
      setDaysList(month_30days);
    }
    setCalendarType(type);
  };
  const returnFullDate = (value, label) => {
    let dateString = '';
    let fullDate = null;
    switch (label) {
      case 'day':
        dateString = `${selectedYear}/${selectedMonth}/${value}`;
        break;
      case 'month':
        dateString = `${selectedYear}/${value}/${selectedDay}`;
        break;
      case 'year':
        dateString = `${value}/${selectedMonth}/${selectedDay}`;
        break;
      default:
        dateString = `${selectedYear}/${selectedMonth}/${selectedDay}`;
        break;
    }
    if (calendarType === 'jalali') {
      fullDate = new Date(
        momentjs(dateString, 'jYYYY/jM/jD').format('YYYY/MM/DD'),
      );
    } else {
      fullDate = new Date(dateString);
    }
    return fullDate;
  };
  //#endregion actions

  return (
    <div className="w-100 d-flex flex-column select-date-dropdown-container">
      {label && (
        <label
          className={`w-100 d-flex text-capitalize px-3 pb-2 m-0 gap-1 dropDown-fontSize text-dark ${labelClassName}`}
        >
          <b>{formatMessage({ id: label })}</b>
          <b>
            {formatMessage({
              id: `booking.${
                calendarType === 'jalali' ? 'shamsiShamsi' : 'miladi'
              }`,
            })}
          </b>
        </label>
      )}
      <div
        className={`w-100 d-flex align-items-center border-identify-form-elemnt rounded-4 px-3 bg-third-light heightResponsive ${containerClassName}`}
      >
        <div className="col-1 h-100 d-flex align-items-center p-0">
          <CalendarIcon
            color={colors.secondary}
            size={18}
            variant="svg-smaller svg-sm-normal"
          />
        </div>
        <div
          className={`col-11 h-100 d-flex align-items-center p-0 ${
            language.name === 'fa' ? 'flex-row' : 'flex-row-reverse'
          }`}
        >
          <span
            className={`${
              language.name === 'fa' ? 'col-3 border-left' : 'col-4'
            } h-100 d-flex align-items-center border-third px-0`}
          >
            <Select
              classname="w-100"
              name="day"
              placeholder={formatMessage({ id: 'booking.day' })}
              options={{
                data: daysList,
                value: 'value',
                label: 'label',
              }}
              style={{ border: 'none' }}
              onChange={handleDayChange}
              selectedValue={selectedDay}
            />
          </span>
          <span
            className={`${
              language.name === 'fa'
                ? 'col-3 border-left'
                : 'col-4 border-right'
            } h-100 d-flex align-items-center border-third px-0`}
          >
            <Select
              classname="w-100"
              name="month"
              placeholder={formatMessage({ id: 'booking.month' })}
              options={{
                data: monthsList,
                value: 'value',
                label: 'label',
              }}
              style={{ border: 'none' }}
              onChange={handleMonthChange}
              selectedValue={selectedMonth}
            />
          </span>
          <span
            className={`${
              language.name === 'fa'
                ? 'col-3 border-left'
                : 'col-4 border-right'
            } h-100 d-flex align-items-center border-third px-0`}
          >
            <Select
              classname="w-100"
              name="year"
              placeholder={formatMessage({ id: 'booking.year' })}
              options={{
                data: yearsList,
                value: 'value',
                label: 'label',
              }}
              style={{ border: 'none' }}
              onChange={handleYearChange}
              selectedValue={selectedYear}
            />
          </span>
          <Conditional checkRender={language.name === 'fa'}>
            <span className="col-3 d-flex flex-column align-items-center justify-content-between font-small font-lg-smaller font-xl-msmall text-third">
              <b
                className={`cursor-pointer ${
                  calendarType === 'jalali' ? 'text-secondary' : 'text-third'
                }`}
                onClick={() => handleCalendarTypeChange('jalali')}
              >
                {formatMessage({ id: 'booking.shamsiShamsi' })}
              </b>
              <b
                className={`cursor-pointer ${
                  calendarType === 'gregorian' ? 'text-secondary' : 'text-third'
                }`}
                onClick={() => handleCalendarTypeChange('gregorian')}
              >
                {formatMessage({ id: 'booking.miladi' })}
              </b>
            </span>
          </Conditional>
        </div>
      </div>
    </div>
  );
};
